/*
 This file is used to inject external scripts into the DOM.
 Injected scripts are:
  - Cookie Policy
 */

import { type ExternalScript } from "./types.ts";

const injectExternalScript = ({ src = "", attributes, defer = false }: ExternalScript): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.defer = defer;
    if (attributes) {
      attributes.forEach((attr) => script.setAttribute(attr.name, attr.value));
    }
    script.onload = () => resolve();
    script.onerror = (error) => reject(error);
    document.head.appendChild(script);
  });
};

const injectCookiePolicy = async () => {
  await injectExternalScript({
    src: "//policy.app.cookieinformation.com/uc.js",
    attributes: [
      {
        name: "data-culture",
        value: "EN"
      }
    ]
  });
};

export const injectExternalScripts = async () => {
  try {
    await injectCookiePolicy();
  } catch (error) {
    console.error("Failed to inject external Cookie Policy", error);
  }
};

// Tracks whether the Plausible script was injected with auto-tracking enabled.
// Used by the router to avoid double-counting pageviews.
let _autoTrackEnabled = false;

const injectPlausibleScript = (
  isUserInternal?: boolean,
  customerCode?: string,
  language?: string,
  autoTrack = false
) => {
  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN;
  const scriptSrc = import.meta.env.VITE_PLAUSIBLE_SCRIPT_SRC;
  if (!domain || !scriptSrc) return;

  _autoTrackEnabled = autoTrack;

  const script = document.createElement("script");
  script.defer = true;
  script.setAttribute("data-domain", domain);
  // event-* attributes are the Plausible API for injecting custom properties into every event.
  // These are not standard HTML data-* attributes — Plausible specifically reads this prefix.
  if (typeof isUserInternal !== "undefined") {
    script.setAttribute("event-userType", String(isUserInternal ? "internal" : "external"));
  }
  if (typeof customerCode === "string" && customerCode.trim() !== "") {
    script.setAttribute("event-customerCode", customerCode);
  }
  if (typeof language === "string" && language.trim() !== "") {
    script.setAttribute("event-language", language);
  }
  script.src = scriptSrc;
  document.head.appendChild(script);

  const inlineScript = document.createElement("script");
  inlineScript.innerHTML =
    "window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }";
  document.head.appendChild(inlineScript);
};

const isAutoTrackEnabled = () => _autoTrackEnabled;

const trackEvent = (eventName: string, props?: Record<string, string | number | boolean>) => {
  try {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      window.plausible(eventName, { props });
    }
  } catch {
    // Analytics failures must not affect the UI
  }
};

const userAnalyticsService = {
  injectExternalScripts,
  injectPlausibleScript,
  isAutoTrackEnabled,
  trackEvent
};

export type * from "./types";

export default userAnalyticsService;
